import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Hook to manage browser notifications
 * @returns {Object} object containing permission state, request function, and send function
 */
export const useBrowserNotifications = () => {
    const [permission, setPermission] = useState(
        typeof window !== 'undefined' && 'Notification' in window
            ? Notification.permission
            : 'default'
    );

    useEffect(() => {
        if (typeof window !== 'undefined' && 'Notification' in window) {
            setPermission(Notification.permission);
        }
    }, []);

    const requestPermission = useCallback(async () => {
        if (!('Notification' in window)) {
            toast.error('Notifiche non supportate', {
                description: 'Il tuo browser non supporta le notifiche desktop.'
            });
            return false;
        }

        try {
            const result = await Notification.requestPermission();
            setPermission(result);

            if (result === 'granted') {
                toast.success('Notifiche attivate! 🔔', {
                    description: 'Ora riceverai ordini direttamente dal Don.'
                });

                // Send a test notification
                new Notification('Benvenuto in Famiglia 🤌', {
                    body: 'Le notifiche sono attive. Non ignorarle mai.',
                    icon: '/favicon.png' // Assuming favicon exists, or use a specific icon
                });
                return true;
            } else {
                toast.warning('Permesso negato', {
                    description: 'Il Don non potrà contattarti. Attento...'
                });
                return false;
            }
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }, []);

    const sendNotification = useCallback((title, options = {}) => {
        if (!('Notification' in window)) return;

        if (Notification.permission === 'granted') {
            try {
                // Build options with defaults
                const notificationOptions = {
                    icon: '/favicon.png', // Default icon
                    badge: '/favicon.png', // Small badge
                    requireInteraction: true, // Keep it visible until user interacts
                    ...options
                };

                // If service worker is ready, use showNotification for better background handling on some devices
                if (navigator.serviceWorker && navigator.serviceWorker.ready) {
                    navigator.serviceWorker.ready.then(registration => {
                        registration.showNotification(title, notificationOptions);
                    });
                } else {
                    // Fallback to standard API
                    new Notification(title, notificationOptions);
                }
            } catch (e) {
                console.error("Failed to send notification:", e);
            }
        }
    }, []);

    return {
        permission,
        requestPermission,
        sendNotification,
        isSupported: 'Notification' in window
    };
};
